import React, { useCallback, useEffect, useState } from "react";
import BN from "bn.js";
import { PublicKey } from "@solana/web3.js";
import { NATIVE_MINT } from "@solana/spl-token";
import { fromBaseUnits, openingPrice } from "@oxminter/shared/amounts";
import { pct } from "@oxminter/shared/feeconfig";
import { describeCountdown, isOpen } from "@oxminter/shared/schedule";
import { useApp } from "../state";
import { Addr, Field, Note, RequireWallet, Stat } from "../components/common";
import { ataFor, readMintFacts } from "../lib/token";
import { readTokenBalanceRaw } from "../lib/tx";
import { getPoolInfoWithRetry, loadRaydium } from "../lib/raydium";
import { readMetadata } from "../lib/metadata";

interface View {
  mint: string;
  symbol: string;
  decimals: number;
  supplyRaw: string;
  mintAuthority: string | null;
  freezeAuthority: string | null;
  metadataName?: string;
  metadataUri?: string;
  metadataMutable?: boolean;
  walletRaw: string;
  poolId?: string;
  lpMint?: string;
  tokenReserveRaw?: string;
  solReserveRaw?: string;
  priceSol?: string;
  tradeFeeRate?: string;
  creatorFeeRate?: string;
  enableCreatorFee?: boolean;
  openTime?: string;
  /** The cluster clock at the time of the read — the one the program uses. */
  clusterNow?: number;
  lpSupplyRaw?: string;
  walletLpRaw?: string;
  protocolFeesA?: string;
  fundFeesA?: string;
  creatorFeesA?: string;
  creatorFeesB?: string;
}

export default function Dashboard() {
  const { connection, net, rpcUrl, wallet, token, setToken, say } = useApp();
  const [mintInput, setMintInput] = useState("");
  const [poolInput, setPoolInput] = useState("");
  const [view, setView] = useState<View | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!wallet || !token) return;
    setBusy(true);
    try {
      const mint = new PublicKey(token.mint);
      const facts = await readMintFacts(connection, mint);
      const md = await readMetadata(rpcUrl, mint);
      const walletRaw = (await readTokenBalanceRaw(connection, ataFor(mint, wallet.publicKey))).toString();

      const v: View = {
        mint: token.mint,
        symbol: token.symbol,
        decimals: facts.decimals,
        supplyRaw: facts.supplyRaw,
        mintAuthority: facts.mintAuthority,
        freezeAuthority: facts.freezeAuthority,
        metadataName: md?.name,
        metadataUri: md?.uri,
        metadataMutable: md?.isMutable,
        walletRaw,
      };

      if (token.poolId) {
        const raydium = await loadRaydium(connection, net.cluster, wallet);
        const d = await getPoolInfoWithRetry(() => raydium.cpmm.getRpcPoolInfo(token.poolId!, true));
        const tokenIsA = d.mintA.toBase58() === token.mint;
        const raw = d as unknown as {
          enableCreatorFee?: boolean;
          creatorFeesMintA?: BN;
          creatorFeesMintB?: BN;
          protocolFeesMintA?: BN;
          fundFeesMintA?: BN;
          lpAmount?: BN;
        };
        v.poolId = token.poolId;
        v.lpMint = d.mintLp.toBase58();
        v.tokenReserveRaw = (tokenIsA ? d.baseReserve : d.quoteReserve).toString();
        v.solReserveRaw = (tokenIsA ? d.quoteReserve : d.baseReserve).toString();
        v.priceSol = openingPrice(
          new BN(v.tokenReserveRaw),
          facts.decimals,
          new BN(v.solReserveRaw),
          9
        );
        v.tradeFeeRate = d.configInfo?.tradeFeeRate.toString();
        v.creatorFeeRate = d.configInfo?.creatorFeeRate?.toString();
        v.enableCreatorFee = Boolean(raw.enableCreatorFee);
        v.openTime = d.openTime.toString();
        try {
          const slot = await connection.getSlot("confirmed");
          const t = await connection.getBlockTime(slot);
          if (t) v.clusterNow = t;
        } catch {
          /* leave it undefined; the pill says the clock is unknown rather than guessing */
        }
        v.lpSupplyRaw = raw.lpAmount?.toString();
        v.protocolFeesA = raw.protocolFeesMintA?.toString();
        v.fundFeesA = raw.fundFeesMintA?.toString();
        v.creatorFeesA = raw.creatorFeesMintA?.toString();
        v.creatorFeesB = raw.creatorFeesMintB?.toString();
        v.walletLpRaw = (
          await readTokenBalanceRaw(connection, ataFor(d.mintLp, wallet.publicKey))
        ).toString();
      }
      setView(v);
    } catch (e) {
      say("error", `Dashboard: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [connection, net.cluster, rpcUrl, wallet, token, say]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function load() {
    try {
      const mint = new PublicKey(mintInput.trim());
      const facts = await readMintFacts(connection, mint);
      const md = await readMetadata(rpcUrl, mint);
      let poolId: string | undefined;
      let lpMint: string | undefined;
      if (poolInput.trim()) {
        poolId = new PublicKey(poolInput.trim()).toBase58();
        if (wallet) {
          const raydium = await loadRaydium(connection, net.cluster, wallet);
          const d = await raydium.cpmm.getRpcPoolInfo(poolId, false);
          const sides = [d.mintA.toBase58(), d.mintB.toBase58()];
          if (!sides.includes(mint.toBase58())) {
            throw new Error("that pool does not contain this mint");
          }
          if (!sides.includes(NATIVE_MINT.toBase58())) {
            say("warn", "This pool is not paired with WSOL; swap and liquidity panels assume it is.");
          }
          lpMint = d.mintLp.toBase58();
        }
      }
      setToken({
        mint: mint.toBase58(),
        symbol: md?.symbol || "TOKEN",
        decimals: facts.decimals,
        poolId,
        lpMint,
      });
      say("ok", `Loaded ${md?.symbol ?? mint.toBase58()}`);
    } catch (e) {
      say("error", `Load failed: ${(e as Error).message}`);
    }
  }

  return (
    <RequireWallet>
      <div className="card">
        <h2>Load an existing token</h2>
        <p className="sub">
          Point the console at a mint you already own. The pool address is optional — without it
          the trading panels stay disabled.
        </p>
        <div className="grid">
          <Field label="Mint address">
            <input
              data-testid="in-loadmint"
              value={mintInput}
              onChange={(e) => setMintInput(e.target.value)}
              placeholder="base58 mint"
            />
          </Field>
          <Field label="CPMM pool address (optional)">
            <input
              data-testid="in-loadpool"
              value={poolInput}
              onChange={(e) => setPoolInput(e.target.value)}
              placeholder="base58 pool id"
            />
          </Field>
        </div>
        <button onClick={load} disabled={!mintInput.trim()} data-testid="btn-load">
          Load
        </button>
      </div>

      {!token ? (
        <div className="card">
          <div className="empty">No token selected.</div>
        </div>
      ) : null}

      {view ? (
        <>
          <div className="grid" style={{ marginBottom: 16 }}>
            <Stat label="Symbol" value={view.symbol} />
            <Stat
              label="Total supply"
              value={fromBaseUnits(new BN(view.supplyRaw), view.decimals)}
            />
            <Stat
              label="Your balance"
              value={fromBaseUnits(new BN(view.walletRaw), view.decimals)}
            />
            <Stat label="Price" value={view.priceSol ? `${view.priceSol} SOL` : "no pool"} />
          </div>

          <div className="card">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2>Token</h2>
              <button onClick={refresh} disabled={busy} data-testid="btn-refresh">
                {busy ? "Refreshing…" : "Refresh"}
              </button>
            </div>
            <dl className="kv">
              <dt>Mint</dt>
              <dd><Addr value={view.mint} /></dd>
              <dt>Decimals</dt>
              <dd>{view.decimals}</dd>
              <dt>Mint authority</dt>
              <dd data-testid="dash-mintauth">
                {view.mintAuthority ? (
                  <>
                    <Addr value={view.mintAuthority} />{" "}
                    <span className="pill warn">supply can still grow</span>
                  </>
                ) : (
                  <span className="pill ok">revoked — supply is fixed</span>
                )}
              </dd>
              <dt>Freeze authority</dt>
              <dd>
                {view.freezeAuthority ? (
                  <>
                    <Addr value={view.freezeAuthority} />{" "}
                    <span className="pill warn">holders can be frozen</span>
                  </>
                ) : (
                  <span className="pill ok">revoked</span>
                )}
              </dd>
              <dt>Metadata</dt>
              <dd>
                {view.metadataName ? (
                  <>
                    {view.metadataName}{" "}
                    <span className={`pill ${view.metadataMutable ? "warn" : "ok"}`}>
                      {view.metadataMutable ? "mutable" : "immutable"}
                    </span>
                  </>
                ) : (
                  <span className="pill danger">missing</span>
                )}
              </dd>
              {view.metadataUri ? (
                <>
                  <dt>Metadata URI</dt>
                  <dd>
                    <a href={view.metadataUri} target="_blank" rel="noreferrer">
                      {view.metadataUri}
                    </a>
                  </dd>
                </>
              ) : null}
            </dl>
          </div>

          {view.poolId ? (
            <div className="card">
              <h2>Pool</h2>
              <dl className="kv">
                <dt>Pool</dt>
                <dd><Addr value={view.poolId} /></dd>
                <dt>LP mint</dt>
                <dd><Addr value={view.lpMint!} /></dd>
                <dt>Reserves</dt>
                <dd data-testid="dash-reserves">
                  {fromBaseUnits(new BN(view.tokenReserveRaw!), view.decimals)} {view.symbol} /{" "}
                  {fromBaseUnits(new BN(view.solReserveRaw!), 9)} SOL
                </dd>
                <dt>LP supply</dt>
                <dd>{view.lpSupplyRaw ?? "—"}</dd>
                <dt>Your LP tokens</dt>
                <dd data-testid="dash-lp">{view.walletLpRaw ?? "0"}</dd>
                <dt>LP trade fee</dt>
                <dd>{view.tradeFeeRate ? pct(Number(view.tradeFeeRate)) : "—"}</dd>
                <dt>Creator fee</dt>
                <dd>
                  {view.creatorFeeRate ? pct(Number(view.creatorFeeRate)) : "—"}{" "}
                  <span className={`pill ${view.enableCreatorFee ? "ok" : "warn"}`}>
                    {view.enableCreatorFee ? "enabled" : "NOT collected"}
                  </span>
                </dd>
                <dt>Protocol / fund fees accrued</dt>
                <dd>
                  {view.protocolFeesA ?? "0"} / {view.fundFeesA ?? "0"} (base side, raw)
                </dd>
                <dt>Creator fees accrued</dt>
                <dd>
                  {view.creatorFeesA ?? "0"} / {view.creatorFeesB ?? "0"} (raw)
                </dd>
                <dt>Trading opens</dt>
                <dd data-testid="pool-opentime">
                  {Number(view.openTime) === 0 ? (
                    "immediately on creation"
                  ) : (
                    <>
                      {new Date(Number(view.openTime) * 1000)
                        .toISOString()
                        .replace("T", " ")
                        .slice(0, 19)}
                      Z · unix {view.openTime}{" "}
                      <span
                        className={`pill ${
                          view.clusterNow !== undefined &&
                          isOpen(Number(view.openTime), view.clusterNow)
                            ? "ok"
                            : "warn"
                        }`}
                      >
                        {view.clusterNow === undefined
                          ? "cluster clock unknown"
                          : isOpen(Number(view.openTime), view.clusterNow)
                            ? "open"
                            : describeCountdown(Number(view.openTime), view.clusterNow)}
                      </span>
                    </>
                  )}
                </dd>
              </dl>

              {view.enableCreatorFee === false ? (
                <Note kind="warn">
                  <p>
                    <code>enableCreatorFee</code> is false on this pool, so the{" "}
                    {view.creatorFeeRate ? pct(Number(view.creatorFeeRate)) : "configured"} creator
                    fee is <strong>not</strong> being charged and the fee claim panel will collect
                    nothing. This is normal for a pool opened with <code>createPool</code>.
                  </p>
                </Note>
              ) : null}
            </div>
          ) : (
            <div className="card">
              <div className="empty">
                No pool loaded for this token — swap and liquidity panels are disabled.
              </div>
            </div>
          )}
        </>
      ) : null}
    </RequireWallet>
  );
}
